import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  LinearProgress,
  alpha,
  useTheme,
} from "@mui/material";
import { checklistFor, sectionsOf, loadInspection, saveInspection, inspectionProgress } from "./inspection";

/**
 * Physical inspection record for this log. Findings-derived items come first —
 * those are the checks the log itself asked for.
 *
 * `onChange` hands the record up so the RCA export includes it.
 */
const LogInspection = ({ log, findings = [], onChange }) => {
  const theme = useTheme();
  const items = useMemo(() => checklistFor(findings), [findings]);
  const [record, setRecord] = useState({});

  useEffect(() => {
    const loaded = loadInspection(log?.id);
    setRecord(loaded);
    onChange?.(loaded);
    // onChange is a page-level setter; re-running on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [log?.id]);

  const update = (itemId, patch) => {
    const next = { ...record, [itemId]: { ...record[itemId], ...patch } };
    setRecord(next);
    saveInspection(log?.id, next);
    onChange?.(next);
  };

  const { checked, issues, total } = inspectionProgress(items, record);

  return (
    <Box>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
        {checked} of {total} checked{issues > 0 ? ` — ${issues} issue${issues === 1 ? "" : "s"} found` : ""}.
        Saved in this browser only; export the RCA draft to keep it.
      </Typography>
      <LinearProgress
        variant="determinate"
        value={total ? (checked / total) * 100 : 0}
        sx={{ my: 1.5, height: 6, borderRadius: 3 }}
      />

      {sectionsOf(items).map(({ section, items: sectionItems }) => (
        <Box key={section} sx={{ mb: 2.5 }}>
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary, textTransform: "uppercase", letterSpacing: 0.4 }}
          >
            {section}
          </Typography>
          <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
            {sectionItems.map((item) => {
              const entry = record[item.id] || {};
              const isIssue = entry.state === "issue";
              return (
                <Box
                  key={item.id}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${alpha(
                      isIssue ? theme.palette.error.main : theme.palette.divider,
                      isIssue ? 0.35 : 0.6
                    )}`,
                    backgroundColor: isIssue ? alpha(theme.palette.error.main, 0.06) : "transparent",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", flexWrap: "wrap" }}>
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 220, color: theme.palette.text.primary }}>
                      {item.label}
                    </Typography>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={entry.state || "unchecked"}
                      onChange={(e, value) => update(item.id, { state: value || "unchecked" })}
                    >
                      <ToggleButton value="ok" sx={{ textTransform: "none", px: 1.25 }}>
                        OK
                      </ToggleButton>
                      <ToggleButton value="issue" sx={{ textTransform: "none", px: 1.25 }}>
                        Issue
                      </ToggleButton>
                      <ToggleButton value="na" sx={{ textTransform: "none", px: 1.25 }}>
                        N/A
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  {entry.state && entry.state !== "unchecked" && (
                    <TextField
                      fullWidth
                      size="small"
                      variant="standard"
                      placeholder="What you saw — measurements, part numbers, photos taken"
                      value={entry.note || ""}
                      onChange={(e) => update(item.id, { note: e.target.value })}
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default LogInspection;
